'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, Mail } from 'lucide-react';
// Use next-auth session directly on signin pages (they render outside the app's UserContext)

import { UserContextProvider, useUserContext } from '../(context)/UserContext';
import { CommonUtils } from '../(common-lib)/CommonUtils';
import { usePageLoading } from '../(ui)/context/LoadingContext';
import ScreenLayout from '../(ui)/layout/ScreenLayout';
import { sendSignInLink } from '../(ui-lib)/InviteUser';
import { UserClient } from '../(ui-lib)/UserClient';
import { isDevelopmentMode } from '../(ui-lib)/Utils';

function SignInWrapper({ isAutoSigningIn, setIsAutoSigningIn }: { isAutoSigningIn: boolean; setIsAutoSigningIn: (value: boolean) => void }) {
  const { status } = useUserContext();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { setLoadingStatus, loadingTextDefault } = usePageLoading();
  const [internalUserError, setInternalUserError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const autoSignInAttemptedRef = useRef(false);

  // Error messages for different error types
  const errorMessages: Record<string, string> = {
    AccessDenied: "Sign in failed. You are not authorized to access this application.",
    OAuthSignin: "Sign in failed. OAuth configuration is incorrect. Contact support.",
    OAuthCallback: "Sign in failed. OAuth configuration is incorrect. Contact support.",
    EmailSignin: "Sign in failed. Please check your email address and try again.",
    default: "Sign in failed. Please try again or contact support.",
  };
  
  // Get parameters from URL
  const email = searchParams?.get('email') || '';
  const redirectUrl = searchParams?.get('redirect') || '/loanapp';
  const error = searchParams?.get('error') || '';

   // Shared helper to validate email and initiate sign-in
   const signInWithValidatedEmail = useCallback(async (emailToUse: string, redirectTo: string): Promise<{ status: 'internal' | 'not_found' | 'ok' | 'error' }> => {
    try {
      const validationResult = await UserClient.validateEmail(emailToUse);

      // If in development mode, override the results
      if (isDevelopmentMode()) {
        validationResult.isInternalUser = false;
      }

      // If in development mode, override the results
      if (isDevelopmentMode()) {
        validationResult.isInternalUser = false;
      }

      if (validationResult.isInternalUser) {
        return { status: 'internal' };
      }
      if (!validationResult.userExists) {
        return { status: 'not_found' };
      }
      
      await sendSignInLink(emailToUse, redirectTo);

      return { status: 'ok' };
    } catch (err) {
      CommonUtils.logError('Error during sign-in flow:', err);
      return { status: 'error' };
    }
  }, []);
  
  useEffect(() => {
    // If already authenticated, redirect to the target URL
    if (status === 'authenticated') {
      router.push(redirectUrl);
      return;
    }

    // Show loading state while checking session
    if (status === 'loading') {
      setLoadingStatus(true);
      loadingTextDefault();
      return;
    }

    // Auto sign-in if email is provided and not already trying to sign in
    if (email && isAutoSigningIn && status === 'unauthenticated' && !autoSignInAttemptedRef.current) {
      autoSignInAttemptedRef.current = true;
      setIsAutoSigningIn(true); 
      setLoadingStatus(true);

      signInWithValidatedEmail(email, redirectUrl)
        .then((result) => {
          if (result.status === 'internal') {
            setInternalUserError(true);
            setIsAutoSigningIn(false);
            setLoadingStatus(false);
            return;
          }
          if (result.status === 'not_found') {
            setLoadingStatus(false);
            router.push('/signin/error');
            return;
          }
          if (result.status === 'error') {
            setIsAutoSigningIn(false);
            setLoadingStatus(false);
            return;
          }
          if (result.status === 'ok') {
            // Email link sent successfully – show verification page
            setIsAutoSigningIn(false);            
            setLoadingStatus(false);
            router.push(`/signin/verify-email?email=${encodeURIComponent(email)}`);
          }
        })
        .catch((err) => {
          CommonUtils.logError('Auto sign-in failed:', err);
          setLoadingStatus(false);
          setIsAutoSigningIn(false);
        });
    } else {
      setLoadingStatus(false);
    }
  }, [email, redirectUrl, status, isAutoSigningIn, setLoadingStatus, loadingTextDefault, setIsAutoSigningIn, router, signInWithValidatedEmail]);

  // Handle form submission for manual sign-in
  async function handleSignIn(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    
    setIsSubmitting(true);
    try {
      const result = await signInWithValidatedEmail(email, redirectUrl);

      if (result.status === 'internal') {
        setInternalUserError(true);
        return;
      }

      if (result.status === 'not_found') {
        setLoadingStatus(false);
        router.push('/signin/error');
        return;
      }

      if (result.status === 'ok') {
        router.push(`/signin/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }

      // 'error' falls through
    } catch (err) {
      CommonUtils.logError('Manual sign-in failed:', err);
    } finally {
      // Only reset submitting state if we didn't navigate away
      setIsSubmitting(false);
    }
  }

  // If auto-signing in, show loading message
  if (isAutoSigningIn) {
    return (
      <div className="max-w-md mx-auto mt-8 space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900">
            Signing you in...
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we sign you in with your email address.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[480px] mx-auto mt-8 sm:mt-12">
      <div className="text-center px-4">
        <p className="signin-eyebrow">
          Client Login
        </p>
        <h2 className="signin-portal-title">
          Stormfield Capital Loan Portal
        </h2>
      </div>

      <div className="mt-8 bg-[#f6f8fa] border border-[#CCD6DE] rounded-[22px] overflow-hidden">
        <div className="px-7 sm:px-10 py-8">
          {error && (
            <p className="bg-red-50 text-red-600 font-semibold p-3 mb-4 loanapp-radius">
              {errorMessages[error] || errorMessages.default}
            </p>
          )}
          {internalUserError && (
            <p className="text-sm bg-red-50 text-red-600 loanapp-radius px-3 py-2 mb-4">
              Stormfield Staff should sign in using the <a href="/signin/internal" className="underline font-medium">internal sign-in page</a>.
            </p>
          )}

          <form onSubmit={handleSignIn}>
            <label htmlFor="email" className="block text-[16px] leading-none font-semibold text-[#3C6684] mb-4">
              Email Address
            </label>
            <div className="mb-5">
              <input
                id="email"
                name="email"
                type="email"
                defaultValue={email}
                required
                className="w-full px-5 py-3 text-[16px] border border-[#C9D2DA] rounded-[12px] bg-white text-[#3f5465] placeholder:text-[#8A97A3] focus:outline-none focus:ring-2 focus:ring-[#3C6684]/20 focus:border-[#9EB2C1]"
                placeholder="jane@example.com"
              />
            </div>

            <p className="signin-helper-text">
              We use passwordless login - enter your email and we&apos;ll send you a secure one-time sign-in link. No password needed.
            </p>

            <button
              type="submit"
              disabled={isSubmitting}
              className="signin-primary-button"
            >
              <span>{isSubmitting ? 'Sending...' : 'Send Sign-In Link'}</span>
              <Mail className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div className="bg-[#edf1f4] border-t border-[#D9E0E6] px-6 py-4 text-center">
          <p className="sm:text-[16px] text-[#667B8C]">
            Don&apos;t have an account?{' '}
            <a
              href="/prequal"
              className="inline-flex items-center gap-2 font-semibold text-[#3C6684] hover:text-[#2e5068]"
            >
              Apply now <ChevronRight className="w-5 h-5" />
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
  
// Keep SignInPage as the layout shell and add Suspense around the wrapper ---
export default function SignInPage() {
  // Remove useSearchParams from here and use state lifting instead
  const [isAutoSigningIn, setIsAutoSigningIn] = useState(false);
  
  return (
    <ScreenLayout
      showBackButton={false}
      title=""
      hideMarketingPanel
      useWideLayout
    >
      <Suspense fallback={<div className="p-4 text-center">Loading...</div>}>
        <UserContextProvider>
          <SignInWrapperWithParams 
            isAutoSigningIn={isAutoSigningIn} 
            setIsAutoSigningIn={setIsAutoSigningIn} 
          />
        </UserContextProvider>
      </Suspense>
    </ScreenLayout>
  );  
}

// New component that safely uses useSearchParams inside Suspense
function SignInWrapperWithParams({ isAutoSigningIn, setIsAutoSigningIn }: { isAutoSigningIn: boolean; setIsAutoSigningIn: (value: boolean) => void }) {
  const searchParams = useSearchParams();
  const email = searchParams?.get('email') || '';
  const autoSignInInitiatedRef = useRef(false);

  // Initialize auto sign-in based on email presence (only once)
  useEffect(() => {
    // Only trigger auto-sign-in if we have an email, haven't already initiated it, and aren't currently auto-signing in
    if (email && !autoSignInInitiatedRef.current && !isAutoSigningIn) {
      autoSignInInitiatedRef.current = true;
      setIsAutoSigningIn(true);
    }
  }, [email, isAutoSigningIn, setIsAutoSigningIn]);

  return (
    <SignInWrapper
      isAutoSigningIn={isAutoSigningIn}
      setIsAutoSigningIn={setIsAutoSigningIn}
    />
  );
}
